import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { PostCard } from "../components/PostCard";
import { apiEndpoints } from "../utils/api";
import { Breadcrumbs } from "../components/Breadcrumbs";

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    category: "",
    dateRange: "",
  });
  const searchInputRef = useRef(null);

  // Leer parámetros de URL al cargar
  useEffect(() => {
    const query = searchParams.get("q") || "";
    const page = parseInt(searchParams.get("page") || "1");
    const category = searchParams.get("category") || "";
    const dateRange = searchParams.get("date") || "";

    setSearchQuery(query);
    setCurrentPage(page);
    setFilters({ category, dateRange });

    // Si hay query, realizar búsqueda
    if (query.trim()) {
      performSearch(query.trim(), page, { category, dateRange });
    }

    // Focus en el input si no hay query
    if (!query && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchParams]);

  // Cargar categorías al montar
  useEffect(() => {
    fetch(apiEndpoints.categories.list())
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data) {
          setCategories(data.data);
        }
      })
      .catch(error => console.error('Error cargando categorías:', error));
  }, []);

  // Función para realizar búsqueda
  const performSearch = async (query, page = 1, searchFilters = filters) => {
    if (!query || query.length < 3) {
      setError('La búsqueda debe tener al menos 3 caracteres');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Construir URL de búsqueda
      const searchUrl = apiEndpoints.posts.search(query);
      //console.log('Realizando búsqueda:', searchUrl);

      const response = await fetch(searchUrl);
      const data = await response.json();

      //console.log('Respuesta de búsqueda:', data);

      if (data.success) {
        // Simular paginación del lado del cliente (ya que la API no la soporta)
        const allResults = data.data || [];
        const resultsPerPage = 12;
        const startIndex = (page - 1) * resultsPerPage;
        const endIndex = startIndex + resultsPerPage;
        const paginatedResults = allResults.slice(startIndex, endIndex);

        setSearchResults({
          data: paginatedResults,
          pagination: {
            page: page,
            limit: resultsPerPage,
            total: allResults.length,
            total_pages: Math.ceil(allResults.length / resultsPerPage)
          },
          allResults: allResults // Guardar todos los resultados para filtrado
        });
      } else {
        setError(data.message || 'Error en la búsqueda');
        setSearchResults(null);
      }
    } catch (err) {
      console.error('Error en búsqueda:', err);
      setError('Error de conexión. Intenta de nuevo.');
      setSearchResults(null);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros del lado del cliente
  const applyFilters = (results, searchFilters) => {
    let filteredResults = [...results];

    // Filtrar por categoría
    if (searchFilters.category) {
      filteredResults = filteredResults.filter(post => 
        post.category_slug === searchFilters.category ||
        post.category_name?.toLowerCase().includes(searchFilters.category.toLowerCase())
      );
    }

    // Filtrar por fecha (implementación mejorada)
    if (searchFilters.dateRange) {
      const now = new Date();
      const filterDate = new Date();

      switch (searchFilters.dateRange) {
        case 'today':
          filterDate.setDate(now.getDate() - 1);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          return filteredResults;
      }

      filteredResults = filteredResults.filter(post => {
        // Obtener la fecha del post con validación
        const dateString = post.published_at || post.created_at || post.date;
        
        if (!dateString) {
          return false; // Si no hay fecha, excluir del filtro
        }

        // Intentar crear fecha de diferentes formatos
        let postDate;
        
        // Si viene en formato MySQL (YYYY-MM-DD HH:mm:ss)
        if (dateString.includes('-')) {
          postDate = new Date(dateString);
        } 
        // Si viene como timestamp
        else if (!isNaN(dateString)) {
          postDate = new Date(parseInt(dateString) * 1000);
        }
        // Otros formatos
        else {
          postDate = new Date(dateString);
        }

        // Verificar que la fecha es válida
        if (isNaN(postDate.getTime())) {
          console.warn(`Fecha inválida en post ${post.id}:`, dateString);
          return false; // Si la fecha no es válida, excluir
        }

        return postDate >= filterDate;
      });
    }

    return filteredResults;
  };

  // Actualizar parámetros de URL
  const updateSearchParams = (newQuery, newPage = 1, newFilters = filters) => {
    const params = new URLSearchParams();

    if (newQuery?.trim()) {
      params.set("q", newQuery.trim());
    }

    if (newPage > 1) {
      params.set("page", newPage.toString());
    }

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key === 'dateRange' ? 'date' : key, value);
      }
    });

    setSearchParams(params);
  };

  // Manejar envío de búsqueda
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setError('La búsqueda debe tener al menos 3 caracteres');
      return;
    }

    setCurrentPage(1);
    updateSearchParams(searchQuery, 1, filters);
  };

  // Manejar cambio de página
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    updateSearchParams(searchQuery, newPage, filters);
    window.scrollTo({ top: 0, behavior: "smooth" });
    
    // Re-aplicar filtros con nueva página
    if (searchResults?.allResults) {
      const filteredResults = applyFilters(searchResults.allResults, filters);
      const resultsPerPage = 12;
      const startIndex = (newPage - 1) * resultsPerPage;
      const endIndex = startIndex + resultsPerPage;
      const paginatedResults = filteredResults.slice(startIndex, endIndex);

      setSearchResults(prev => ({
        ...prev,
        data: paginatedResults,
        pagination: {
          ...prev.pagination,
          page: newPage
        }
      }));
    }
  };

  // Manejar cambio de filtros
  const handleFilterChange = (filterKey, value) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    updateSearchParams(searchQuery, 1, newFilters);

    // Re-aplicar filtros inmediatamente
    if (searchResults?.allResults) {
      const filteredResults = applyFilters(searchResults.allResults, newFilters);
      const resultsPerPage = 12;
      const paginatedResults = filteredResults.slice(0, resultsPerPage);

      setSearchResults(prev => ({
        ...prev,
        data: paginatedResults,
        pagination: {
          page: 1,
          limit: resultsPerPage,
          total: filteredResults.length,
          total_pages: Math.ceil(filteredResults.length / resultsPerPage)
        }
      }));
    }
  };

  // Limpiar filtros
  const clearFilters = () => {
    const newFilters = { category: "", dateRange: "" };
    setFilters(newFilters);
    setCurrentPage(1);
    updateSearchParams(searchQuery, 1, newFilters);
  };

  // Sugerencias de búsqueda
  const searchSuggestions = [
    "política", "economía", "deportes", "cultura", "tecnología",
    "salud", "educación", "medio ambiente", "arauca", "colombia"
  ];

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Búsqueda", url: "/buscar" },
    ...(searchQuery ? [{
      name: `"${searchQuery}"`,
      url: `/buscar?q=${encodeURIComponent(searchQuery)}`
    }] : [])
  ];

  const hasActiveFilters = Object.values(filters).some(filter => filter);
  const posts = searchResults?.data || [];
  const pagination = searchResults?.pagination || {};

  return (
    <>
      <SEOHead
        title={searchQuery ? `Resultados para "${searchQuery}"` : "Buscar noticias"}
        description={searchQuery 
          ? `Resultados de búsqueda para "${searchQuery}" en Red Arauca`
          : "Busca noticias, artículos y contenido en Red Arauca"
        }
        noindex={!searchQuery}
      />

      <div className="search-page">
        {/* Header con búsqueda */}
        <header className="page-header">
          <div className="header-container-left">
            <div className="header-content-left">
              <h1 className="display-4 fw-bold mb-0 mt-4">
                {searchQuery ? `Resultados para "${searchQuery}"` : "Buscar noticias"}
              </h1>
              
              <div className="breadcrumbs-section-header">
                <Breadcrumbs items={breadcrumbItems} />
              </div>

              <div className="header-search">
                <form onSubmit={handleSearch} className="search-form-header">
                  <div className="search-input-group-header">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Buscar noticias, artículos..."
                      className="search-input-header"
                      autoComplete="off"
                    />
                    <button
                      type="submit"
                      className="search-button-header"
                      disabled={!searchQuery.trim() || loading}
                    >
                      {loading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-search"></i>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </header>

        <div className="container">
          {/* Sugerencias si no hay búsqueda */}
          {!searchQuery && (
            <section className="search-suggestions-section">
              <h3>Búsquedas populares</h3>
              <div className="search-suggestions">
                {searchSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setSearchQuery(suggestion);
                      updateSearchParams(suggestion, 1, filters);
                    }}
                    className="suggestion-tag"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Mostrar error si existe */}
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
              <button onClick={() => setError(null)} className="close-error">
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}

          {/* Resultados de búsqueda */}
          {searchQuery && !error && (
            <>
              {/* Estadísticas y filtros */}
              <div className="search-controls">
                <div className="search-stats">
                  {loading ? (
                    <span>Buscando...</span>
                  ) : (
                    <span>
                      {pagination.total > 0 ? (
                        `${pagination.total} ${pagination.total === 1 ? 'resultado' : 'resultados'}`
                      ) : (
                        "No se encontraron resultados"
                      )}
                    </span>
                  )}
                </div>

                {/* Filtros */}
                <div className="search-filters">
                  <div className="filter-group">
                    <select
                      value={filters.category}
                      onChange={(e) => handleFilterChange("category", e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Todas las categorías</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.slug}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <select
                      value={filters.dateRange}
                      onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Cualquier fecha</option>
                      <option value="today">Hoy</option>
                      <option value="week">Esta semana</option>
                      <option value="month">Este mes</option>
                      <option value="year">Este año</option>
                    </select>
                  </div>

                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="clear-filters">
                      <i className="fas fa-times"></i>
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>

              {/* Resultados */}
              <section className="search-results">
                {loading ? (
                  <LoadingSkeleton />
                ) : posts.length > 0 ? (
                  <>
                    <div className="results-grid">
                      {posts.map((post) => (
                        <PostCard
                          key={post.id || post.slug}
                          post={post}
                          variant="search"
                          searchQuery={searchQuery}
                        />
                      ))}
                    </div>

                    {/* Paginación */}
                    {pagination.total_pages > 1 && (
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={pagination.total_pages}
                        onPageChange={handlePageChange}
                        loading={loading}
                      />
                    )}
                  </>
                ) : (
                  <NoResultsState
                    query={searchQuery}
                    onNewSearch={(newQuery) => {
                      setSearchQuery(newQuery);
                      updateSearchParams(newQuery, 1, {});
                    }}
                  />
                )}
              </section>
            </>
          )}
        </div>

        <style jsx>{`
          .search-page {
            background: #f8fafc;
            min-height: 100vh;
            padding: 0 0 60px;
          }

          .page-header {
            background: linear-gradient(
                to left,
                transparent 50%,
                rgba(0, 0, 0, 0.6) 100%
              ),
              url("https://img.freepik.com/premium-vector/searching-mode_46706-110.jpg?w=2000");
            background-size: cover;
            background-position: center;
            color: white;
            height: 220px;
            position: relative;
            display: flex;
            align-items: center;
            margin: 0;
          }

          .page-header::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.4);
          }

          .page-header .header-container-left {
            position: relative;
            z-index: 2;
            width: 100%;
            padding-left: 40px;
            padding-right: 20px;
          }

          .header-content-left {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            height: 100%;
            text-align: left;
            max-width: 600px;
          }

          .page-header h1 {
            font-size: 2.5rem;
            font-weight: 800;
            margin: 0;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
            opacity: 0.95;
            line-height: 1.2;
          }

          .breadcrumbs-section-header {
            margin: 16px 0 8px 0;
          }

          .breadcrumbs-section-header nav {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }

          .breadcrumbs-section-header .breadcrumb-link {
            color: rgba(255, 255, 255, 0.9) !important;
            text-decoration: underline;
          }

          .breadcrumbs-section-header .breadcrumb-link:hover {
            color: white !important;
          }

          .breadcrumbs-section-header .breadcrumb-current {
            color: rgba(255, 255, 255, 0.8) !important;
            font-weight: 600;
          }

          .header-search {
            margin-top: 12px;
            width: 100%;
            max-width: 500px;
          }

          .search-input-group-header {
            display: flex;
            gap: 0;
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.2);
            border-radius: 12px;
            overflow: hidden;
            backdrop-filter: blur(5px);
          }

          .search-input-header {
            flex: 1;
            padding: 14px 18px;
            border: none;
            background: transparent;
            font-size: 1rem;
            color: white;
          }

          .search-input-header::placeholder {
            color: rgba(255, 255, 255, 0.7);
          }

          .search-button-header {
            padding: 14px 18px;
            background: rgba(255, 255, 255, 0.2);
            color: white;
            border: none;
            cursor: pointer;
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px 20px 0;
          }

          .search-suggestions-section {
            background: white;
            border-radius: 16px;
            padding: 30px;
            margin-bottom: 30px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            text-align: center;
          }

          .search-suggestions-section h3 {
            color: #1f2937;
            margin-bottom: 20px;
            font-size: 1.5rem;
            font-weight: 600;
          }

          .search-suggestions {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: center;
          }

          .suggestion-tag {
            padding: 8px 16px;
            background: #f3f4f6;
            border: 1px solid #e5e7eb;
            border-radius: 20px;
            color: #374151;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .suggestion-tag:hover {
            background: #3b82f6;
            color: white;
            transform: translateY(-1px);
          }

          .error-message {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px 20px;
            background: #fef2f2;
            border: 1px solid #fecaca;
            border-radius: 8px;
            color: #dc2626;
            margin-bottom: 20px;
          }

          .close-error {
            background: none;
            border: none;
            color: #dc2626;
            cursor: pointer;
            margin-left: auto;
          }

          .search-controls {
            background: white;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 20px;
          }

          .search-stats {
            color: #6b7280;
            font-weight: 500;
          }

          .search-filters {
            display: flex;
            gap: 12px;
            align-items: center;
            flex-wrap: wrap;
          }

          .filter-select {
            padding: 8px 12px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            background: white;
            color: #374151;
            min-width: 150px;
          }

          .clear-filters {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            background: #ef4444;
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
          }

          .results-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
            gap: 30px;
            margin-bottom: 40px;
          }

          @media (max-width: 768px) {
            .page-header h1 {
              font-size: 1.8rem;
            }
            
            .results-grid {
              grid-template-columns: 1fr;
            }
            
            .search-controls {
              flex-direction: column;
              align-items: stretch;
            }
          }
        `}</style>
      </div>
    </>
  );
};

// Componentes auxiliares
const PaginationControls = ({ currentPage, totalPages, onPageChange, loading }) => {
  return (
    <nav className="pagination-container">
      <button
        className="pagination-button"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || loading}
      >
        <i className="fas fa-chevron-left"></i>
        Anterior
      </button>

      <span className="pagination-info">
        Página {currentPage} de {totalPages}
      </span>

      <button
        className="pagination-button"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || loading}
      >
        Siguiente
        <i className="fas fa-chevron-right"></i>
      </button>

      <style jsx>{`
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          margin-top: 40px;
          padding: 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .pagination-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .pagination-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .pagination-button:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }

        .pagination-info {
          color: #6b7280;
          font-weight: 500;
        }
      `}</style>
    </nav>
  );
};

const LoadingSkeleton = () => (
  <div className="results-grid">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <div
        key={i}
        className="loading-skeleton"
        style={{
          height: "300px",
          borderRadius: "12px",
          background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
          backgroundSize: "200% 100%",
          animation: "loading 1.5s infinite"
        }}
      ></div>
    ))}
    <style jsx>{`
      @keyframes loading {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);

const NoResultsState = ({ query, onNewSearch }) => (
  <div className="no-results">
    <div className="no-results-icon">
      <i className="fas fa-search"></i>
    </div>
    <h3>No se encontraron resultados</h3>
    <p>No encontramos noticias que coincidan con "<strong>{query}</strong>"</p>

    <div className="suggestions">
      <h4>Sugerencias:</h4>
      <ul>
        <li>Verifica la ortografía de las palabras</li>
        <li>Usa términos más generales</li>
        <li>Prueba con sinónimos</li>
        <li>Reduce el número de palabras</li>
      </ul>
    </div>

    <div className="alternative-searches">
      <h4>Búsquedas relacionadas:</h4>
      <div className="alternative-tags">
        {["política", "economía", "deportes", "arauca"].map((suggestion) => (
          <button
            key={suggestion}
            onClick={() => onNewSearch(suggestion)}
            className="alternative-tag"
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>

    <style jsx>{`
      .no-results {
        text-align: center;
        padding: 60px 20px;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }

      .no-results-icon {
        font-size: 4rem;
        color: #d1d5db;
        margin-bottom: 20px;
      }

      .no-results h3 {
        color: #374151;
        margin-bottom: 12px;
      }

      .no-results p {
        color: #6b7280;
        margin-bottom: 30px;
      }

      .suggestions {
        margin-bottom: 30px;
        text-align: left;
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }

      .suggestions h4 {
        color: #374151;
        margin-bottom: 15px;
        text-align: center;
      }

      .suggestions ul {
        list-style: none;
        padding: 0;
      }

      .suggestions li {
        padding: 4px 0;
        color: #6b7280;
      }

      .suggestions li:before {
        content: "• ";
        color: #3b82f6;
        font-weight: bold;
      }

      .alternative-searches h4 {
        color: #374151;
        margin-bottom: 15px;
      }

      .alternative-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        justify-content: center;
      }

      .alternative-tag {
        padding: 8px 16px;
        background: #f3f4f6;
        border: 1px solid #e5e7eb;
        border-radius: 20px;
        color: #374151;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .alternative-tag:hover {
        background: #3b82f6;
        color: white;
        border-color: #3b82f6;
      }
    `}</style>
  </div>
);